"use client"

import React, { useRef, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import Icon from "../../Icon"

// ─── Scroll Reveal Hook ───────────────────────────────────────────────────────

function useScrollReveal(threshold = 0.12) {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true) },
      { threshold }
    )
    obs.observe(el)
    return () => obs.unobserve(el)
  }, [threshold])
  return [ref, visible]
}

// ─── Navigation ───────────────────────────────────────────────────────────────

function Navigation() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener("scroll", onScroll)
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  return (
    <nav className={`w-full bg-white/95 backdrop-blur-sm sticky top-0 z-50 transition-all duration-300 border-b ${
      scrolled ? "border-gray-200/80 shadow-sm" : "border-transparent"
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          <Link to="/" className="flex items-center gap-3 group">
            <img src="/logofinalrounded.png" alt="RecordLOGS" className="w-8 h-8 rounded-lg object-cover shadow-sm group-hover:shadow-md transition-shadow" />
            <span className="text-xl font-bold text-slate-800">RecordLOGS</span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/available-units" className="text-slate-600 hover:text-green-600 transition-colors font-medium text-sm">
              Search Units
            </Link>
            <Link to="/user-guide" className="text-slate-600 hover:text-green-600 transition-colors font-medium text-sm">
              User Guide
            </Link>
            <Link to="/login" className="bg-green-600 text-white px-5 py-2 rounded-lg hover:bg-green-700 transition-all duration-200 font-semibold text-sm shadow-sm hover:shadow-md ring-1 ring-green-500/20">
              Sign In
            </Link>
          </div>

          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
            aria-label="Toggle menu"
          >
            <Icon name={menuOpen ? "x" : "list"} size="1.25rem" />
          </button>
        </div>

        {menuOpen && (
          <div className="md:hidden border-t border-gray-100 py-3 animate-slide-in-right">
            <div className="flex flex-col gap-1">
              <Link to="/available-units" className="px-3 py-2.5 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors text-sm flex items-center gap-2" onClick={() => setMenuOpen(false)}>
                <Icon name="search" size="0.875rem" /> Search Units
              </Link>
              <Link to="/user-guide" className="px-3 py-2.5 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors text-sm flex items-center gap-2" onClick={() => setMenuOpen(false)}>
                <Icon name="book" size="0.875rem" /> User Guide
              </Link>
              <Link to="/login" className="px-3 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm text-center font-semibold flex items-center justify-center gap-2" onClick={() => setMenuOpen(false)}>
                <Icon name="box-arrow-in-right" size="0.875rem" /> Sign In
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

const HERO_LINE1 = ["The", "All-In-One"]
const HERO_LINE2 = ["Record", "Management", "Platform"]

const HERO_MODULES = [
  { icon: "buildings",    label: "Rentals",   delay: "0.1s"  },
  { icon: "people-fill",  label: "Groups",    delay: "0.25s" },
  { icon: "car-front",    label: "Vehicles",  delay: "0.4s"  },
  { icon: "person-badge", label: "Members",   delay: "0.55s" },
  { icon: "grid-1x2-fill",label: "Dashboard", delay: "0.7s"  },
]

function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-green-300 via-white to-green-50 min-h-screen flex items-center">
      <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: "url('/images/bgimage.jpg')" }} />

      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="hero-blob-1 absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-green-400/25 blur-3xl" />
        <div className="hero-blob-2 absolute -bottom-40 -right-40 w-[450px] h-[450px] rounded-full bg-green-300/25 blur-3xl" />
        <div className="hero-blob-3 absolute top-1/2 left-1/2 w-[350px] h-[350px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-200/20 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-36 w-full">
        <div className="text-center">

          {/* Eyebrow */}
          <div className="hero-in-1 inline-flex items-center gap-2 bg-white/80 border border-green-200 rounded-full px-3.5 py-1.5 sm:px-4 sm:py-2 mb-6 sm:mb-8 shadow-sm backdrop-blur-sm">
            <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
            <span className="text-xs sm:text-sm font-semibold text-green-700">Record Management Platform</span>
          </div>

          {/* Mobile headline */}
          <div className=" mb-6">
            <h1 className="word-reveal text-[2.6rem] font-black text-slate-800 leading-[1.08] tracking-tight" style={{ animationDelay: "0.15s" }}>
              The All-In-One
            </h1>
            <h1 className="word-reveal text-[2.6rem] font-black leading-[1.08] tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-emerald-600" style={{ animationDelay: "0.3s" }}>
              Record Platform
            </h1>
          </div>

          {/* Desktop headline — word-by-word reveal */}
          <div className="hidden sm:block">
            <h1 className="text-6xl lg:text-7xl font-black text-slate-800 leading-none mb-2">
              {HERO_LINE1.map((w, i) => (
                <span key={w} className="word-reveal inline-block mr-3 lg:mr-4" style={{ animationDelay: `${0.15 + i * 0.09}s` }}>
                  {w}
                </span>
              ))}
            </h1>
            <h1 className="text-6xl lg:text-7xl font-black leading-none mb-8">
              {HERO_LINE2.map((w, i) => (
                <span key={w} className="word-reveal inline-block mr-3 lg:mr-4 text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-emerald-600" style={{ animationDelay: `${0.33 + i * 0.09}s` }}>
                  {w}
                </span>
              ))}
            </h1>
          </div>

          {/* Mobile description */}
          <p className="hero-in-2  text-base text-slate-600 max-w-xs mx-auto leading-relaxed px-2">
            One platform for rentals, groups, vehicles, and members.
          </p>

          {/* Desktop description */}
          <p className="hero-in-2 hidden sm:block text-lg lg:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Managing members, rentals, groups, vehicles, and more — all in one organized platform designed for modern record-keeping.
          </p>

          {/* CTAs */}
          <div className="hero-in-3 mt-10 flex flex-wrap justify-center gap-3 sm:gap-4">
            <Link
              to="/available-units"
              className="group inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-white font-semibold rounded-full px-6 py-3.5 sm:px-8 sm:py-4 text-sm sm:text-base shadow-lg shadow-green-600/25 hover:shadow-xl hover:shadow-green-400/30 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
            >
              <Icon name="search" size="0.875rem" className="shrink-0 opacity-90" />
              Search Available Units
              <Icon name="arrow-right" className="transition-transform group-hover:translate-x-1 shrink-0" size="0.875rem" />
            </Link>
            <Link
              to="/login"
              className="group inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-800 font-semibold rounded-full px-6 py-3.5 sm:px-8 sm:py-4 text-sm sm:text-base shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
            >
              <Icon name="box-arrow-in-right" size="0.875rem" className="shrink-0 text-green-600" />
              Sign Up / Log In
            </Link>
          </div>

          {/* Module display — scroll shelf on mobile, float pills on sm+ */}
          <div className="hero-in-4 mt-12 sm:mt-14">

            {/* Mobile: horizontal scroll shelf */}
            <div className="sm:hidden module-strip-fade -mx-4 px-4 overflow-x-auto no-scrollbar">
              <div className="flex gap-3 w-max mx-auto pb-1">
                {HERO_MODULES.map(({ icon, label }) => (
                  <div key={label} className="bg-white/90 backdrop-blur-sm border border-green-200/80 rounded-2xl px-4 py-3.5 flex flex-col items-center gap-2 shadow-sm w-[72px] shrink-0">
                    <div className="w-9 h-9 bg-green-50 rounded-xl flex items-center justify-center">
                      <Icon name={icon} className="text-green-600" size="1rem" />
                    </div>
                    <span className="text-[11px] font-semibold text-slate-700 text-center leading-tight">{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Desktop: floating pills */}
            <div className="hidden sm:flex flex-wrap justify-center gap-3">
              {HERO_MODULES.map(({ icon, label, delay }) => (
                <div key={label} className="float-badge bg-white/90 backdrop-blur-sm border border-green-200/70 rounded-full px-4 py-2 flex items-center gap-2 shadow-sm" style={{ animationDelay: delay }}>
                  <Icon name={icon} className="text-green-600" size="0.875rem" />
                  <span className="text-sm font-medium text-slate-700">{label}</span>
                </div>
              ))}
            </div>

          </div>

        </div>
      </div>
    </section>
  )
}

// ─── Resources ────────────────────────────────────────────────────────────────

const RESOURCES = [
  { icon: "book-fill",   title: "User Guide",     desc: "Step-by-step documentation to help you get the most out of RecordLOGS.", to: "/user-guide",     cta: "Read the guide" },
  { icon: "shield-lock", title: "Privacy Policy", desc: "Understand how we collect, use, and protect your organization's data.",  to: "/privacy-policy", cta: "View policy" },
]

function ResourcesSection() {
  const [ref, visible] = useScrollReveal()
  return (
    <section className="py-14 bg-white" ref={ref}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {RESOURCES.map(({ icon, title, desc, to, cta }, i) => (
            <Link
              key={title}
              to={to}
              className={`group bg-white border border-slate-200 rounded-2xl p-7 hover:border-green-300 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 block ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${i * 0.15}s` }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center group-hover:bg-green-100 group-hover:scale-110 transition-all duration-300 shrink-0">
                  <Icon name={icon} className="text-green-600" size="1.125rem" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 mb-1.5">{title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-3">{desc}</p>
                  <span className="text-green-600 font-medium text-sm flex items-center gap-1.5 group-hover:gap-3 transition-all duration-300">
                    {cta}
                    <Icon name="arrow-right" size="0.8rem" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── Mobile App ───────────────────────────────────────────────────────────────

function MobileAppSection() {
  const [ref, visible] = useScrollReveal()
  const googlePlayUrl = "https://play.google.com/store/apps/details?id=com.recordlogs.app"
  return (
    <section className="py-14 bg-white" ref={ref}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-10 lg:p-16 text-center relative overflow-hidden transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="absolute -top-16 -left-16 w-56 h-56 rounded-full bg-green-500/10 blur-3xl pointer-events-none" aria-hidden="true" />
          <div className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full bg-green-400/10 blur-3xl pointer-events-none" aria-hidden="true" />
          <div className="relative">
            <div className="w-16 h-16 bg-green-500/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <Icon name="phone-fill" className="text-green-400" size="1.75rem" />
            </div>
            <p className="text-green-400 font-semibold text-xs uppercase tracking-widest mb-3">Available on Android</p>
            <h3 className="text-3xl lg:text-4xl font-black text-white mb-4">RecordLOGS Mobile App</h3>
            <p className="text-slate-300 text-lg mb-8 max-w-md mx-auto">
              Manage your records on the go with the RecordLOGS Android app.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => alert("App Store link coming soon!")} className="inline-flex items-center gap-3 bg-white text-slate-800 px-6 py-3 rounded-xl hover:bg-slate-100 transition-colors font-medium">
                <Icon name="apple" size="1.25rem" />
                <div className="text-left">
                  <div className="text-xs text-slate-500">Download on the</div>
                  <div className="font-semibold text-sm">App Store</div>
                </div>
              </button>
              <a href={googlePlayUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-white text-slate-800 px-6 py-3 rounded-xl hover:bg-slate-100 transition-colors font-medium">
                <Icon name="google-play" size="1.25rem" />
                <div className="text-left">
                  <div className="text-xs text-slate-500">Get it on</div>
                  <div className="font-semibold text-sm">Google Play</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// ─── Footer ───────────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="bg-slate-800 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 pb-10 border-b border-slate-700">
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img src="/logofinalrounded.png" alt="RecordLOGS" className="w-9 h-9 rounded-lg object-cover" />
              <span className="text-xl font-bold">RecordLOGS</span>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed max-w-xs">
              Your all-in-one record management platform for rentals, groups, vehicles, and members.
            </p>
          </div>
          <div>
            <p className="font-semibold text-xs text-slate-400 uppercase tracking-wider mb-4">Platform</p>
            <ul className="space-y-2">
              <li><Link to="/available-units" className="text-slate-400 hover:text-green-400 transition-colors text-sm">Available Units</Link></li>
              <li><Link to="/login" className="text-slate-400 hover:text-green-400 transition-colors text-sm">Sign In</Link></li>
              <li><a href="https://play.google.com/store/apps/details?id=com.recordlogs.app" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-green-400 transition-colors text-sm">Get the Android app</a></li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-xs text-slate-400 uppercase tracking-wider mb-4">Resources</p>
            <ul className="space-y-2">
              <li><Link to="/user-guide" className="text-slate-400 hover:text-green-400 transition-colors text-sm">User Guide</Link></li>
              <li><Link to="/privacy-policy" className="text-slate-400 hover:text-green-400 transition-colors text-sm">Privacy Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">© {new Date().getFullYear()} RecordLOGS. All rights reserved.</p>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-slate-400 text-sm">All systems operational</span>
          </div>
        </div>
      </div>
    </footer>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────

function HomepageView() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main>
        <HeroSection />
        <ResourcesSection />
        <MobileAppSection />
      </main>
      <Footer />
    </div>
  )
}

export default HomepageView
